import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import ModalPortal from "../Modal/ModalPortal"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../Titles/Titles"
import {
  faArrowsLeftRight,
  faArrowRotateLeft,
  faCompress,
  faCropSimple,
  faExpand,
  faRotateLeft,
  faRotateRight,
  faXmark,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import THEME from "../../configuration/Theme"
import Button, { ButtonIcon } from "../Button/Button"
import style from "./InputPhoto.module.css"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { SIZE } from "../../configuration/Sizes"
import GetSpace from "../../utils/space"

export interface CropState {
  file: File
  src: string
}

type CropMode = "cover" | "contain"

type Rotation = 0 | 90 | 180 | 270

const resolveTargetSize = (source: { width: number; height: number }, profile: { width: number; height: number }) => {
  const canKeepPreferredSize = source.width >= profile.width && source.height >= profile.height

  if (canKeepPreferredSize) {
    return { width: profile.width, height: profile.height }
  }

  return { width: 512, height: 512 }
}

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = reject
    image.src = src
  })

export const buildCroppedFile = async (
  file: File,
  profile: { width: number; height: number },
  zoom: number,
  horizontalPosition: number,
  verticalPosition: number,
  rotation: Rotation = 0,
  cropMode: CropMode = "cover",
  flipHorizontal = false,
) => {
  const sourceUrl = URL.createObjectURL(file)

  const source = await loadImage(sourceUrl)

  URL.revokeObjectURL(sourceUrl)

  const rotatedCanvas = document.createElement("canvas")

  const swapsDimensions = rotation === 90 || rotation === 270

  rotatedCanvas.width = swapsDimensions ? source.height : source.width

  rotatedCanvas.height = swapsDimensions ? source.width : source.height

  const rotatedContext = rotatedCanvas.getContext("2d")

  if (!rotatedContext) {
    return null
  }

  rotatedContext.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2)

  rotatedContext.rotate((rotation * Math.PI) / 180)

  rotatedContext.scale(flipHorizontal ? -1 : 1, 1)

  rotatedContext.drawImage(source, -source.width / 2, -source.height / 2)

  const targetSize = resolveTargetSize(rotatedCanvas, profile)

  const canvas = document.createElement("canvas")

  canvas.width = targetSize.width

  canvas.height = targetSize.height

  const ctx = canvas.getContext("2d")

  if (!ctx) {
    return null
  }

  ctx.imageSmoothingEnabled = true

  ctx.imageSmoothingQuality = "high"

  ctx.fillStyle = "#ffffff"

  ctx.fillRect(0, 0, targetSize.width, targetSize.height)

  const widthScale = targetSize.width / rotatedCanvas.width

  const heightScale = targetSize.height / rotatedCanvas.height

  const baseScale = cropMode === "contain" ? Math.min(widthScale, heightScale) : Math.max(widthScale, heightScale)

  const drawWidth = rotatedCanvas.width * baseScale * zoom

  const drawHeight = rotatedCanvas.height * baseScale * zoom

  const drawX = (horizontalPosition / 100) * (targetSize.width - drawWidth)

  const drawY = (verticalPosition / 100) * (targetSize.height - drawHeight)

  ctx.drawImage(rotatedCanvas, drawX, drawY, drawWidth, drawHeight)

  return new Promise<File | null>((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          resolve(null)
          return
        }

        const fileName = file.name.replace(/\.[^/.]+$/, "") + ".jpg"
        resolve(new File([blob], fileName, { type: "image/jpeg" }))
      },
      "image/jpeg",
      0.92,
    )
  })
}

export const InputPhotoCropModal: React.FC<{
  cropState: CropState
  profile: { width: number; height: number; label: string }
  onCancel: () => void
  onConfirm: (file: File | null) => void
}> = ({ cropState, onCancel, onConfirm, profile }) => {
  const { t } = useTranslation("inputPhoto")

  const [zoom, setZoom] = useState(1)

  const [horizontalPosition, setHorizontalPosition] = useState(50)

  const [verticalPosition, setVerticalPosition] = useState(50)

  const [rotation, setRotation] = useState<Rotation>(0)

  const [cropMode, setCropMode] = useState<CropMode>("cover")

  const [flipHorizontal, setFlipHorizontal] = useState(false)

  const [previewUrl, setPreviewUrl] = useState<string>(cropState.src)

  const ratio = useMemo(() => profile.width / profile.height, [profile.height, profile.width])

  const resetAdjustments = () => {
    setZoom(1)
    setHorizontalPosition(50)
    setVerticalPosition(50)
    setRotation(0)
    setCropMode("cover")
    setFlipHorizontal(false)
  }

  const showEntireImage = () => {
    setZoom(1)
    setHorizontalPosition(50)
    setVerticalPosition(50)
    setCropMode("contain")
  }

  useEffect(() => {
    let isMounted = true

    const updatePreview = async () => {
      const file = await buildCroppedFile(
        cropState.file,
        profile,
        zoom,
        horizontalPosition,
        verticalPosition,
        rotation,
        cropMode,
        flipHorizontal,
      )

      if (!isMounted || !file) {
        return
      }

      const url = URL.createObjectURL(file)
      setPreviewUrl((old) => {
        if (old.startsWith("blob:")) {
          URL.revokeObjectURL(old)
        }

        return url
      })
    }

    updatePreview()

    return () => {
      isMounted = false
    }
  }, [cropMode, cropState.file, flipHorizontal, horizontalPosition, profile, rotation, verticalPosition, zoom])

  useEffect(() => {
    return () => {
      if (previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  const confirmSelection = async () => {
    const file = await buildCroppedFile(
      cropState.file,
      profile,
      zoom,
      horizontalPosition,
      verticalPosition,
      rotation,
      cropMode,
      flipHorizontal,
    )
    onConfirm(file)
  }

  const confirmDefaultSelection = async () => {
    const file = await buildCroppedFile(cropState.file, profile, 1, 50, 50)
    onConfirm(file)
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <section className={`${defaultStyle.alert1} ${style.cropModalPopup}`}>
            <TitlePageResponsiveIconSmall
              title={t("label_crop_title")}
              icon={faCropSimple}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faXmark}
                    onClick={onCancel}
                    size="small"
                    descriptionKey="interactionDescriptions:components.inputPhoto.inputPhotoCropModal.button.onCancel"
                  />
                </div>
              }
            />

            <GetSpace top={5} />

            <div className={style.cropPreview} style={{ aspectRatio: `${ratio}` }}>
              <img src={previewUrl} alt={t("label_crop_preview_alt")} className={style.cropPreviewImage} />
            </div>

            <div className={defaultStyle.divisionBar}></div>

            <div className={style.cropControls}>
              <div className={style.cropToolbar} role="group" aria-label={t("label_crop_tools")}>
                <button
                  type="button"
                  onClick={() => setRotation((current) => ((current + 270) % 360) as Rotation)}
                  data-interaction-description-key="interactionDescriptions:components.inputPhoto.inputPhotoCropModal.button.setRotationCurrentCurrent270360AsRotation"
                >
                  <FontAwesomeIcon icon={faRotateLeft} />
                  <span>{t("button_rotate_left")}</span>
                </button>

                <button
                  type="button"
                  onClick={() => setRotation((current) => ((current + 90) % 360) as Rotation)}
                  data-interaction-description-key="interactionDescriptions:components.inputPhoto.inputPhotoCropModal.button.setRotationCurrentCurrent90360AsRotation"
                >
                  <FontAwesomeIcon icon={faRotateRight} />
                  <span>{t("button_rotate_right")}</span>
                </button>

                <button
                  type="button"
                  onClick={() => setFlipHorizontal((current) => !current)}
                  data-interaction-description-key="interactionDescriptions:components.inputPhoto.inputPhotoCropModal.button.setFlipHorizontalCurrentCurrent"
                >
                  <FontAwesomeIcon icon={faArrowsLeftRight} />
                  <span>{t("button_flip_horizontal")}</span>
                </button>

                <button
                  type="button"
                  onClick={resetAdjustments}
                  data-interaction-description-key="interactionDescriptions:components.inputPhoto.inputPhotoCropModal.button.resetAdjustments"
                >
                  <FontAwesomeIcon icon={faArrowRotateLeft} />
                  <span>{t("button_reset_crop")}</span>
                </button>
              </div>

              <div className={style.cropModeSelector} role="group" aria-label={t("label_crop_mode")}>
                <button
                  type="button"
                  aria-pressed={cropMode === "cover"}
                  className={cropMode === "cover" ? style.cropModeActive : ""}
                  onClick={() => setCropMode("cover")}
                  data-interaction-description-key="interactionDescriptions:components.inputPhoto.inputPhotoCropModal.button.setCropModeCover"
                >
                  <FontAwesomeIcon icon={faExpand} />
                  <span>{t("button_crop_fill")}</span>
                </button>

                <button
                  type="button"
                  aria-pressed={cropMode === "contain"}
                  className={cropMode === "contain" ? style.cropModeActive : ""}
                  onClick={showEntireImage}
                  data-interaction-description-key="interactionDescriptions:components.inputPhoto.inputPhotoCropModal.button.showEntireImage"
                >
                  <FontAwesomeIcon icon={faCompress} />
                  <span>{t("button_crop_fit")}</span>
                </button>
              </div>

              <label>
                <span className={style.sliderLabel}>
                  <span>{t("label_zoom")}</span>

                  <b>{zoom.toFixed(1)}x</b>
                </span>

                <input
                  type="range"
                  min={0.1}
                  max={5}
                  step={0.05}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  data-interaction-description-key="interactionDescriptions:components.inputPhoto.inputPhotoCropModal.input.input"
                />
                <small className={style.cropControlHint}>{t("label_zoom_range_hint")}</small>
              </label>

              <label>
                <span className={style.sliderLabel}>
                  <span>{t("label_horizontal")}</span>

                  <b>{Math.round(horizontalPosition)}%</b>
                </span>

                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={horizontalPosition}
                  onChange={(e) => setHorizontalPosition(Number(e.target.value))}
                  data-interaction-description-key="interactionDescriptions:components.inputPhoto.inputPhotoCropModal.input.input2"
                />
              </label>

              <label>
                <span className={style.sliderLabel}>
                  <span>{t("label_vertical")}</span>

                  <b>{Math.round(verticalPosition)}%</b>
                </span>

                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={verticalPosition}
                  onChange={(e) => setVerticalPosition(Number(e.target.value))}
                  data-interaction-description-key="interactionDescriptions:components.inputPhoto.inputPhotoCropModal.input.input3"
                />
              </label>
            </div>

            <div className={defaultStyle.divisionBar}></div>

            <div className={defaultStyle.buttonsController}>
              <Button
                label={t("button_use_default_crop")}
                onClick={confirmDefaultSelection}
                bgColor={THEME.colors.buttonBGPlusColor}
                descriptionKey="interactionDescriptions:components.inputPhoto.inputPhotoCropModal.button.useDefaultCrop"
              />

              <div style={{ width: "8px" }} />

              <Button
                label={t("button_apply_crop")}
                onClick={confirmSelection}
                bgColor={THEME.colors.buttonBGSaveColor}
                descriptionKey="interactionDescriptions:components.inputPhoto.inputPhotoCropModal.button.applyCrop"
              />
            </div>
          </section>
        </Animate>
      </div>
    </ModalPortal>
  )
}
